import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import awsRegionsList from "aws-regions/regions.json";
import { CloudProviderContext, getAccountCloudType } from "../../../../CloudProviderContext";
import { useAccounts } from "../../../../components/Common/HelperComponents/GetDataFromStore";
import { addAuditAccount, listAllCatalogFrameworks, unwrapPost } from "../../../../helpers/auditHub_helper";
import { AWS_ACCOUNT_NUMBER, MAX_ACCOUNT_REGIONS } from "../constants";
import { frameworkLabel } from "../utils";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, auditHubPageSx } from "../theme";

const AWS_REGION_CODES = (Array.isArray(awsRegionsList) ? awsRegionsList : [])
  .map((region) => region?.code)
  .filter((code) => typeof code === "string" && code);

const LABEL_COLOR = "#334155";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    minHeight: 40,
    fontSize: "0.875rem",
    color: LABEL_COLOR,
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: `0 0 0 1px ${AH_TEXT}` },
  },
  "& .MuiInputBase-input": { fontSize: "0.875rem", color: LABEL_COLOR },
  "& .MuiInputBase-input::placeholder": { color: "#74788d", opacity: 1 },
};

const chipSx = {
  height: 24,
  borderRadius: "6px",
  backgroundColor: "#fff4eb",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  fontSize: "0.8125rem",
  fontWeight: 500,
  "& .MuiChip-label": { px: 1 },
  "& .MuiChip-deleteIcon": {
    color: "#9a3412",
    fontSize: 16,
    "&:hover": { color: AH_WHITE, backgroundColor: AH_ACCENT },
  },
};

const regionChipSx = (selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
  color: AH_TEXT,
  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
});

const optionPaperSx = {
  borderRadius: "8px",
  mt: 0.5,
  fontSize: "0.875rem",
  "& .MuiAutocomplete-option.Mui-focused": { backgroundColor: "#fff4eb" },
  "& .MuiAutocomplete-option[aria-selected='true']": { backgroundColor: "#fff4eb" },
};

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const FieldLabel = ({ children, htmlFor, required, hint }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: "6px" }}>
    <Box component="label" htmlFor={htmlFor} sx={{ fontSize: "0.8125rem", fontWeight: 600, color: LABEL_COLOR }}>
      {children}
      {required ? (
        <Box component="span" sx={{ color: "#dc2626", ml: 0.25 }} aria-hidden="true">
          *
        </Box>
      ) : null}
    </Box>
    {hint ? <Typography sx={{ fontSize: 12, color: AH_MUTED, whiteSpace: "nowrap" }}>{hint}</Typography> : null}
  </Box>
);

const accountOptionLabel = (account) => account?.accountName || account?.accountCode || account?.accountNumber || "";

const manualCountText = (count) =>
  Number.isInteger(count) ? `${count} manual control${count === 1 ? "" : "s"}` : "";

const CATALOG_CONCURRENCY = 4;

const catalogIds = (items) =>
  (Array.isArray(items) ? items : []).map((fw) => Number(fw.complianceAddonId)).filter(Number.isInteger);

/**
 * `frameworks` is the audit's own framework list. When it is non-empty the account inherits it, and only
 * accounts whose catalog offers it are listed; an audit with no framework yet picks one per account.
 */
const AddAccountDialog = ({ auditId, existingAccountNumbers, frameworks, isLive, open, onClose, onAdded }) => {
  const accounts = useAccounts();
  const { selectedProvider } = useContext(CloudProviderContext);
  const [account, setAccount] = useState(null);
  const [regions, setRegions] = useState([]);
  const [framework, setFramework] = useState(null);
  const [catalog, setCatalog] = useState({ status: "idle", items: [], error: "" });
  const [eligibility, setEligibility] = useState({});
  const [eligibilityRetry, setEligibilityRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const eligibilityRef = useRef({});
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    setAccount(null);
    setRegions([]);
    setFramework(null);
    setCatalog({ status: "idle", items: [], error: "" });
    setSaving(false);
    setError("");
  }, [open]);

  const auditFrameworks = useMemo(
    () => (Array.isArray(frameworks) ? frameworks : []).filter((fw) => Number.isInteger(Number(fw?.complianceAddonId))),
    [frameworks]
  );
  const fixedIds = useMemo(() => catalogIds(auditFrameworks), [auditFrameworks]);
  const fixed = fixedIds.length > 0;
  const fixedNames = auditFrameworks.map((fw) => frameworkLabel(fw)).join(", ");

  const accountOptions = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    const taken = new Set(existingAccountNumbers || []);
    return accounts
      .filter((item) => {
        if (item.accountNumber?.includes("INIT00000001")) return false;
        if (item.accountNumber?.includes("TEST")) return false;
        if (!AWS_ACCOUNT_NUMBER.test(item.accountNumber || "")) return false;
        if (taken.has(item.accountNumber)) return false;
        return getAccountCloudType(item) === selectedProvider;
      })
      .sort((a, b) => accountOptionLabel(a).localeCompare(accountOptionLabel(b)));
  }, [accounts, existingAccountNumbers, selectedProvider]);

  const candidateKey = accountOptions.map((item) => item.accountNumber).join(",");

  // Each account's catalog is the eligibility POST /audits/{id}/accounts enforces. Answers are kept
  // across opens; a failed one is asked again on the next open or on Retry.
  useEffect(() => {
    if (!open || !fixed) return;
    const pending = candidateKey.split(",").filter((number) => {
      const status = eligibilityRef.current[number]?.status;
      return number && status !== "loading" && status !== "succeeded";
    });
    if (!pending.length) return;
    pending.forEach((number) => {
      eligibilityRef.current[number] = { status: "loading", ids: [] };
    });
    setEligibility({ ...eligibilityRef.current });
    let index = 0;
    const loadNext = async () => {
      while (index < pending.length) {
        const number = pending[index];
        index += 1;
        try {
          const items = await listAllCatalogFrameworks({ accountNumber: number });
          eligibilityRef.current[number] = { status: "succeeded", ids: catalogIds(items) };
        } catch (_error) {
          eligibilityRef.current[number] = { status: "failed", ids: [] };
        }
        if (mountedRef.current) setEligibility({ ...eligibilityRef.current });
      }
    };
    for (let i = 0; i < Math.min(CATALOG_CONCURRENCY, pending.length); i += 1) loadNext();
  }, [open, fixed, candidateKey, eligibilityRetry]);

  const eligibleIdsFor = (number) => {
    const ids = eligibility[number]?.ids || [];
    return fixedIds.filter((id) => ids.includes(id));
  };
  const eligibleOptions = fixed
    ? accountOptions.filter((item) => eligibleIdsFor(item.accountNumber).length > 0)
    : accountOptions;
  const eligibilityStatuses = fixed ? accountOptions.map((item) => eligibility[item.accountNumber]?.status) : [];
  const checkingCount = eligibilityStatuses.filter((status) => !status || status === "loading").length;
  const failedCount = eligibilityStatuses.filter((status) => status === "failed").length;

  const accountNumber = account?.accountNumber;

  useEffect(() => {
    if (!open || fixed || !accountNumber) return undefined;
    let cancelled = false;
    setFramework(null);
    setCatalog({ status: "loading", items: [], error: "" });
    listAllCatalogFrameworks({ accountNumber })
      .then((items) => {
        if (cancelled) return;
        const sorted = items
          .filter((fw) => Number.isInteger(Number(fw.complianceAddonId)))
          .sort((a, b) => frameworkLabel(a).localeCompare(frameworkLabel(b)));
        setCatalog({ status: "succeeded", items: sorted, error: "" });
      })
      .catch((err) => {
        if (!cancelled) setCatalog({ status: "failed", items: [], error: err?.message || "Unable to load frameworks." });
      });
    return () => {
      cancelled = true;
    };
  }, [open, fixed, accountNumber]);

  const toggleRegion = (code) => {
    setRegions((prev) => {
      if (prev.includes(code)) return prev.filter((region) => region !== code);
      if (prev.length >= MAX_ACCOUNT_REGIONS) return prev;
      return [...prev, code];
    });
  };

  const allRegions = regions.length === 0;
  let complianceAddonIds = [];
  if (fixed && accountNumber) complianceAddonIds = eligibleIdsFor(accountNumber);
  else if (!fixed && framework) complianceAddonIds = catalogIds([framework]);
  const canAdd = Boolean(accountNumber) && complianceAddonIds.length > 0 && !saving;

  const confirm = async () => {
    if (!auditId || !canAdd) return;
    setSaving(true);
    setError("");
    const body = { accountNumber, complianceAddonIds };
    if (regions.length) body.regions = regions;
    try {
      const result = unwrapPost(await addAuditAccount(auditId, body));
      onAdded({ account, result });
    } catch (err) {
      setError(err?.message || "Unable to add this account.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="add-account-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 640 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography id="add-account-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Add Account
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: saving ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6 }}>
          {isLive
            ? "The account's scan starts as soon as it is added."
            : "The account is scanned when the assignment is launched."}
        </Typography>

        {fixed ? (
          <Box sx={{ mt: 2 }}>
            <FieldLabel>{auditFrameworks.length > 1 ? "Frameworks" : "Framework"}</FieldLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {auditFrameworks.map((fw) => (
                <Chip key={fw.complianceAddonId} size="small" label={frameworkLabel(fw)} sx={chipSx} />
              ))}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <FieldLabel htmlFor="add-account-picker" required>
            Account
          </FieldLabel>
          <Autocomplete
            size="small"
            id="add-account-picker"
            disabled={saving}
            options={eligibleOptions}
            value={account}
            onChange={(_event, value) => {
              setAccount(value);
              if (error) setError("");
            }}
            getOptionLabel={accountOptionLabel}
            isOptionEqualToValue={(a, b) => a.accountNumber === b.accountNumber}
            loading={fixed && checkingCount > 0}
            loadingText="Checking which accounts support this framework…"
            noOptionsText={
              fixed ? `No other accounts support ${fixedNames}.` : "No other accounts can be added."
            }
            slotProps={{ paper: { sx: optionPaperSx } }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box component="li" key={key} {...optionProps} sx={{ display: "block !important" }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: AH_TEXT }}>
                    {accountOptionLabel(option)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED }}>{option.accountNumber}</Typography>
                </Box>
              );
            }}
            renderInput={(params) => <TextField {...params} placeholder="Select Account" sx={fieldSx} />}
          />
          {fixed && checkingCount > 0 && eligibleOptions.length > 0 ? (
            <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.75 }}>
              Checking {checkingCount} more account{checkingCount === 1 ? "" : "s"}…
            </Typography>
          ) : null}
          {fixed && !checkingCount && failedCount ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 12, color: "#dc2626" }}>
                {failedCount} account{failedCount === 1 ? "" : "s"} could not be checked.
              </Typography>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setEligibilityRetry((n) => n + 1)}
                sx={{ fontSize: 12, color: AH_ACCENT }}
              >
                Retry
              </Link>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ mt: 2 }}>
          <FieldLabel hint={allRegions ? "All regions" : `${regions.length} selected`}>Regions</FieldLabel>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              maxHeight: 136,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            <Chip
              label="All Regions"
              clickable
              disabled={saving}
              aria-pressed={allRegions}
              onClick={() => setRegions([])}
              sx={regionChipSx(allRegions)}
            />
            {AWS_REGION_CODES.map((code) => {
              const selected = regions.includes(code);
              return (
                <Chip
                  key={code}
                  label={code}
                  clickable
                  disabled={saving}
                  aria-pressed={selected}
                  onClick={() => toggleRegion(code)}
                  sx={regionChipSx(selected)}
                />
              );
            })}
          </Box>
        </Box>

        {fixed ? null : (
          <Box sx={{ mt: 2 }}>
            <FieldLabel htmlFor="add-account-frameworks" required>
              Framework
            </FieldLabel>
            {!accountNumber ? (
              <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED }}>Select an account to see its frameworks.</Typography>
            ) : catalog.status === "loading" || catalog.status === "idle" ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={22} sx={{ color: AH_ACCENT }} />
              </Box>
            ) : catalog.status === "failed" ? (
              <Typography role="alert" sx={{ fontSize: "0.8125rem", color: "#dc2626" }}>
                {catalog.error}
              </Typography>
            ) : catalog.items.length === 0 ? (
              <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED }}>
                This account does not support any frameworks.
              </Typography>
            ) : (
              <Autocomplete
                size="small"
                id="add-account-frameworks"
                disabled={saving}
                options={catalog.items}
                value={framework}
                onChange={(_event, value) => {
                  setFramework(value);
                  if (error) setError("");
                }}
                getOptionLabel={(fw) => frameworkLabel(fw)}
                isOptionEqualToValue={(a, b) => Number(a.complianceAddonId) === Number(b.complianceAddonId)}
                slotProps={{ paper: { sx: optionPaperSx } }}
                renderOption={(props, fw) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box component="li" key={key} {...optionProps} sx={{ display: "block !important" }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: AH_TEXT }}>{frameworkLabel(fw)}</Typography>
                      {manualCountText(fw.manualControlCount) ? (
                        <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED }}>
                          {manualCountText(fw.manualControlCount)}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                }}
                renderInput={(params) => <TextField {...params} placeholder="Select Framework" sx={fieldSx} />}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          onClick={confirm}
          disabled={!canAdd}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            ...dialogButtonSx,
            backgroundColor: AH_ACCENT,
            color: AH_WHITE,
            "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: AH_ACCENT, color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {saving ? "Adding" : "Add"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddAccountDialog;
